import React from 'react';
import { Link } from 'react-router-dom';
import { Column } from '../../components/DataTable/DataTable';
import { Appointment } from './useData';
import { FieldConfig } from '../../components/generic/fieldRenderers';
import { FormLayout } from '../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, formatDateTime, APP_CONFIG } from '../../config/appConfig';
import { EntityAttachmentsSection } from '../../components/EntityAttachmentsSection';
import type { ThemeColors } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import Badge from '../../components/ui/Badge';
import { normalizeHexColor, parseStatusColorConfig, resolveStatusTextColor } from '../../utils/statusColor';

export const renderAppointmentStatusBadge = (status?: string, color?: string) => {
  if (!status) {
    return null;
  }

  const parsedConfig = parseStatusColorConfig(color);
  const resolvedColor = normalizeHexColor(parsedConfig.backgroundColor);
  if (!resolvedColor) {
    return (
      <Badge variant="secondary" className="border rounded-md px-2 py-0.5">
        {status}
      </Badge>
    );
  }

  return (
    <Badge
      variant="secondary"
      className="border rounded-md px-2 py-0.5"
      style={{
        backgroundColor: resolvedColor,
        borderColor: resolvedColor,
        color: resolveStatusTextColor(resolvedColor, parsedConfig.textColorMode),
      }}
    >
      {status}
    </Badge>
  );
};

// Module Metadata
export const moduleConfig = {
  // Module Information
  module: {
    title: 'Appointments',
    description: 'Schedule and track client meetings',
    i18nKey: 'crm-appointments',
    permissionModuleKey: 'appointments',
    route: {
      list: '/appointments',
      new: '/appointments/new',
      detail: (id: number) => `/appointments/${id}`,
      edit: (id: number) => `/appointments/${id}/edit`,
    },
    // API Endpoints
    api: {
      list: '/crm-clients-appointments',
      detail: (id: number) => `/crm-clients-appointments/${id}`,
    },
  },

  // Pagination Settings
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },

  // Search Settings
  search: {
    placeholder: 'Search by title, client, or notes...',
  },

  filterGroups: [
    {
      name: 'basic',
      label: 'Filters',
      icon: '🔍',
      fields: [
        'clientAppName', 'clientId', 'assignedTo', 'clientAppStatus', 'clientAppScheduleDateTime',
      ],
    },
  ],

  // Filter Configuration
  filters: {
    fields: [
      {
        key: 'clientAppName',
        label: 'Title',
        placeholder: 'All titles',
        getValue: (item: Appointment) => item.clientAppName,
        options: [],
      },
      {
        key: 'clientId',
        label: 'Client',
        placeholder: 'All organizations',
        type: 'select' as const,
        getValue: (item: Appointment) => 
          item.client?.clientName || (item.clientId ? `Client #${item.clientId}` : ''),
        options: [],
      },
      {
        key: 'assignedTo',
        label: 'Assigned to',
        placeholder: 'All Users',
        type: 'select' as const,
        getValue: (item: Appointment) => 
          item.assignedToUser
            ? (item.assignedToUser.userDisplayName || `${item.assignedToUser.userFirstName || ''}`.trim() || `User #${item.assignedToUser.id}`)
            : (item.assignedTo ? `User #${item.assignedTo}` : ''),
        options: [],
      },
      {
        key: 'clientAppStatus',
        label: 'Status',
        placeholder: 'All Statuses',
        type: 'select' as const,
        getValue: (item: Appointment) => 
          item.clientAppStatusDescr?.as_descr || (item.clientAppStatus ? `Status #${item.clientAppStatus}` : ''),
        options: [],
      },
      {
        key: 'clientAppScheduleDateTime',
        label: 'Date',
        placeholder: 'All dates',
        type: 'date' as const,
        getValue: (item: Appointment) =>
          item.clientAppScheduleDateTime
            ? formatDateGlobal(item.clientAppScheduleDateTime)
            : undefined,
        options: [],
      },
    ],
    // Filter logic functions
    applyFilter: (
      items: Appointment[],
      filters: {
        clientAppName: string[] | string;
        clientId: string[];
        assignedTo: string[];
        clientAppStatus: string[];
        clientAppScheduleDateTime: string[];
      },
      dateRanges?: Record<string, { start: Date | null; end: Date | null }>
    ) => {
      const ranges = dateRanges || {};
      return items.filter((item) => {
        // Appointment Name filter
        const nameFilter = filters.clientAppName;
        if (typeof nameFilter === 'string' && nameFilter.trim() !== '') {
          const value = (item.clientAppName || '').toLowerCase();
          if (!value.includes(nameFilter.toLowerCase())) return false;
        } else if (Array.isArray(nameFilter) && nameFilter.length > 0) {
          const value = (item.clientAppName || '').toLowerCase();
          const matches = nameFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Client filter (matches by display name or ID)
        if (filters.clientId && filters.clientId.length > 0) {
          const displayValue = item.client?.clientName || (item.clientId ? `Client #${item.clientId}` : '');
          const matches = filters.clientId.some((filter) => {
            return displayValue === filter || 
                   (item.clientId && filter === `Client #${item.clientId}`) ||
                   (item.client?.clientName && filter === item.client.clientName);
          });
          if (!matches) return false;
        }

        // Asgn filter (matches by display name or ID)
        if (filters.assignedTo && filters.assignedTo.length > 0) {
          const displayValue = item.assignedToUser
            ? (item.assignedToUser.userDisplayName || `${item.assignedToUser.userFirstName || ''}`.trim() || `User #${item.assignedToUser.id}`)
            : (item.assignedTo ? `User #${item.assignedTo}` : '');
          const matches = filters.assignedTo.some((filter) => {
            return displayValue === filter ||
                   (item.assignedTo && filter === `User #${item.assignedTo}`) ||
                   (item.assignedToUser?.userDisplayName && filter === item.assignedToUser.userDisplayName) ||
                   (item.assignedToUser?.id && filter === `User #${item.assignedToUser.id}`);
          });
          if (!matches) return false;
        }

        // Status filter (matches by description or ID)
        if (filters.clientAppStatus && filters.clientAppStatus.length > 0) {
          const displayValue = item.clientAppStatusDescr?.as_descr || (item.clientAppStatus ? `Status #${item.clientAppStatus}` : '');
          const matches = filters.clientAppStatus.some((filter) => {
            return displayValue === filter ||
                   (item.clientAppStatus && filter === `Status #${item.clientAppStatus}`) ||
                   (item.clientAppStatusDescr?.as_descr && filter === item.clientAppStatusDescr.as_descr);
          });
          if (!matches) return false;
        }

        // Schedule Date filter
        if (filters.clientAppScheduleDateTime.length > 0) {
          if (ranges.clientAppScheduleDateTime) {
            const range = ranges.clientAppScheduleDateTime;
            if (range.start && range.end && item.clientAppScheduleDateTime) {
              const itemDate = new Date(item.clientAppScheduleDateTime);
              if (itemDate < range.start || itemDate > range.end) return false;
            }
          } else {
            const value = item.clientAppScheduleDateTime
              ? formatDateGlobal(item.clientAppScheduleDateTime)
              : '';
            const matches = filters.clientAppScheduleDateTime.some((filter) => {
              return value.includes(filter) || filter.includes(value);
            });
            if (!matches) return false;
          }
        }

        return true;
      });
    },
  },

  // Toolbar Actions Configuration
  toolbar: {
    actions: [
      {
        key: 'download',
        icon: 'download',
        title: 'Download',
        showTitle: false,
        variant: 'default' as const,
        dropdown: [
          {
            key: 'export-excel',
            label: 'Export to Excel',
            icon: 'download',
            onClick: async (exportData?: {
              sortedData: any[];
              tableColumns: any[];
              entityName: string;
              exportFileName: string;
              formatDate?: (dateString?: string) => string;
            }) => {
              if (!exportData) {
                console.error('Export data not provided');
                return;
              }

              try {
                const { exportToExcel } = await import('../../components/exports');
                await exportToExcel(exportData);
              } catch (error: any) {
                console.error('Failed to export to Excel:', error);
                throw new Error(error?.message || 'Failed to export to Excel. Please check the console for details.');
              }
            },
          },
        ],
      },
      {
        key: 'add',
        icon: 'add',
        title: 'Add New Appointment',
        showTitle: false,
        variant: 'primary' as const,
        href: '/appointments/new',
      },
    ],
  },

  // Table Configuration
  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: Appointment) => {
      window.location.href = `/appointments/${item.clientAppId}`;
    },
    keyExtractor: (item: Appointment) => item.clientAppId,
    getClientId: (item: Appointment) => item.clientId ?? null,
  },

  // Empty States
  emptyStates: {
    noData: 'Get started by creating a new appointment.',
    noSearchResults: 'No appointments found matching your search criteria.',
    noFilterResults: 'No appointments match the selected filters.',
  },

  // Delete Confirmation
  delete: {
    title: 'Delete Appointment',
    message: 'Are you sure you want to delete this appointment? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete appointment. Please try again.',
    handler: async (id: number) => {
      const { deleteAppointment } = await import('../../services/mutations');
      await deleteAppointment(id);
    },
  },

  // Utility Functions
  utils: {
    formatDate: (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateGlobal(dateString);
    },
  },

  // Detail View Configuration
  detail: {
    subtitle: 'Appointment Details',
    notFoundTitle: 'Appointment not found',
    notFoundMessage: 'The appointment you\'re looking for doesn\'t exist.',
    notFoundBackText: 'Back to Appointments',
    defaultTitle: 'Unnamed Appointment',
    getTitle: (item: Appointment) => {
      return item.clientAppName || `Appointment #${item.clientAppId}`;
    },
  },

  // Form Configuration
  form: {
    new: {
      title: 'New appointment',
      subtitle: 'Schedule a meeting or call',
      submitText: 'Create',
    },
    edit: {
      title: 'Edit appointment',
      submitText: 'Save',
    },
    cancelText: 'Cancel',
    loadingText: 'Loading appointment...',
    errorMessage: 'Failed to save appointment. Please try again.',
    supportsFiles: true,
    create: async (data: Partial<Appointment>, files?: File[], filesToDetach?: number[]) => {
      const { createAppointment } = await import('../../services/mutations');
      return await createAppointment(data, files);
    },
    update: async (id: number, data: Partial<Appointment>, files?: File[], filesToDetach?: number[]) => {
      const { updateAppointment } = await import('../../services/mutations');
      return await updateAppointment(id, data, files, filesToDetach);
    },
    getViewFileUrl: (entityId: number, fileId: number) =>
      `/crm-clients-appointments/appointment/${entityId}/files/${fileId}`,
  },

  // Unified Field Configuration
  fields: [
    {
      name: 'clientAppName',
      label: 'Title',
      type: 'text',
      required: true,
      placeholder: 'e.g. Client call, site visit',
      validation: {
        required: true,
        minLength: 2,
        maxLength: 255,
      },
    },
    {
      name: 'clientAppScheduleDateTime',
      label: 'Date & time',
      type: 'datetime',
      required: true,
      placeholder: 'When is this appointment?',
      validation: {
        required: true,
      },
    },
    {
      name: 'clientAppDuration',
      label: 'Duration',
      type: 'select',
      placeholder: 'How long?',
      options: [],
      apiEndpoint: '/p-crm-duration',
      labelField: 'pd_descr',
      sortApiList: (items) =>
        [...items].sort(
          (a, b) => (a.pd_order ?? 0) - (b.pd_order ?? 0) || (a.pd_min ?? 0) - (b.pd_min ?? 0),
        ),
    },
    {
      name: 'clientId',
      label: 'Client',
      type: 'select',
      placeholder: 'Optional',
      options: [],
      apiEndpoint: '/crm-clients',
      labelField: 'clientName',
    },
    {
      name: 'assignedTo',
      label: 'Assigned to',
      type: 'select',
      required: true,
      placeholder: 'Who is responsible?',
      options: [],
      apiEndpoint: '/users',
      labelField: 'userDisplayName',
      validation: {
        required: true,
      },
    },
    {
      name: 'clientAppStatus',
      label: 'Status',
      type: 'select',
      required: true,
      placeholder: 'Select status',
      options: [],
      apiEndpoint: '/p-crm-app-status',
      labelField: 'as_descr',
      validation: {
        required: true,
      },
    },
    {
      name: 'clientAppNotes',
      label: 'Notes',
      type: 'textarea',
      placeholder: 'Optional details',
      rows: 3,
      validation: {
        maxLength: 2000,
      },
    },
    {
      name: 'inviteEmails',
      label: 'Invite emails',
      type: 'textarea',
      placeholder: 'External participants (one email per line)',
      rows: 2,
      helpText: 'Calendar invites (.ics) are emailed when the appointment is saved.',
    },
  ] as FieldConfig[],

  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'appointment',
        title: 'Appointment',
        gridCols: 'md:grid-cols-2',
        fields: [
          'clientAppName',
          'clientAppScheduleDateTime',
          'clientAppDuration',
          'clientId',
          'assignedTo',
          'clientAppStatus',
          'clientAppNotes',
          'inviteEmails',
        ],
        fieldSpans: {
          clientAppName: 'md:col-span-2',
          clientAppNotes: 'md:col-span-2',
          inviteEmails: 'md:col-span-2',
        },
        collapsible: false,
      },
    ],
  } as FormLayout,
};

// Helper function to get columns
export const getColumns = (
  formatDate: (dateString?: string) => string,
  _opts?: unknown,
  colors?: ThemeColors
): Column<Appointment>[] => [
  {
    key: 'clientAppName',
    header: 'Name',
    sortable: true,
    render: (value, row) => {
      const name = value ? (value.length > 50 ? value.substring(0, 50) + '...' : value) : '-';
      const hasAttachments = (row.FileAttachment?.length ?? 0) > 0;
      const attachmentCount = row.FileAttachment?.length ?? 0;
      if (!hasAttachments) return name;
      return (
        <span className="inline-flex items-center gap-1.5 min-w-0">
          <span className="truncate">{name}</span>
          <span
            title={`${attachmentCount} file${attachmentCount !== 1 ? 's' : ''} attached`}
            className="shrink-0 inline-flex text-gray-500 dark:text-gray-400"
            aria-hidden
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
            </svg>
          </span>
        </span>
      );
    },
  },
  {
    key: 'client.clientName',
    header: 'Client',
    sortable: true,
    render: (_value, row) => {
      const clientName = row.client?.clientName;
      if (!clientName) return '-';
      return clientName.length > 40 ? `${clientName.substring(0, 40)}…` : clientName;
    },
  },
  {
    key: 'clientAppScheduleDateTime',
    header: 'When',
    sortable: true,
    render: (value) => formatDateTime(value),
  },
  {
    key: 'clientAppDurationMins',
    header: 'Duration',
    sortable: true,
    render: (value, row) => (
      row.clientAppDurationDescr?.pd_descr || (value ? `${value} min` : '-')
    ),
  },
  {
    key: 'clientAppStatus',
    header: 'Status',
    sortable: true,
    render: (value, row) => {
      const status = row.clientAppStatusDescr?.as_descr || (value ? String(value) : '');
      if (!status) return '-';
      return renderAppointmentStatusBadge(status, row.clientAppStatusDescr?.as_color);
    },
  },
];

// Helper function to get detail sections
export const getDetailSections = (
  item: Appointment,
  formatDate: (dateString?: string) => string
) => {
  const assignedLabel = item.assignedToUser
    ? (item.assignedToUser.userDisplayName || `${item.assignedToUser.userFirstName || ''}`.trim() || `User #${item.assignedToUser.id}`)
    : (item.assignedTo ? `User #${item.assignedTo}` : 'Unassigned');

  const durationLabel = item.clientAppDurationDescr
    ? item.clientAppDurationDescr.pd_descr
    : (item.clientAppDurationMins ? `${item.clientAppDurationMins} minutes` : '-');

  const sections = [
    {
      title: 'Appointment',
      icon: '📅',
      fields: [
        { label: 'Title', value: item.clientAppName || '-' },
        { label: 'When', value: formatDateTime(item.clientAppScheduleDateTime) },
        { label: 'Duration', value: durationLabel },
        { label: 'Client', value: item.client?.clientName || (item.clientId ? `Client #${item.clientId}` : '-') },
        { label: 'Assigned to', value: assignedLabel },
        {
          label: 'Status',
          value:
            renderAppointmentStatusBadge(
              item.clientAppStatusDescr?.as_descr || item.clientAppStatus?.toString() || '',
              item.clientAppStatusDescr?.as_color,
            ) || '-',
        },
      ],
    },
  ];

  if (item.clientAppNotes?.trim()) {
    sections.push({
      title: 'Notes',
      icon: '📝',
      fields: [{ label: '', value: item.clientAppNotes }],
    });
  }

  if ((item.FileAttachment?.length ?? 0) > 0) {
    sections.push({
      title: 'Attachments',
      icon: '📎',
      fields: [
        {
          label: '',
          value: React.createElement(EntityAttachmentsSection, {
            downloadEndpoint: `/crm-clients-appointments/appointment/${item.clientAppId}/files`,
            files: item.FileAttachment || [],
            defaultDownloadName: `appointment_${item.clientAppId}_files.zip`,
            getViewFileUrl: (fileId) => `/crm-clients-appointments/appointment/${item.clientAppId}/files/${fileId}`,
          }),
        },
      ],
    });
  }

  return sections;
};

// Helper function to get status badges
export const getStatusBadges = (item: Appointment, _colors?: ThemeColors) => {
  const status = item.clientAppStatusDescr?.as_descr || '';
  return status
    ? renderAppointmentStatusBadge(status, item.clientAppStatusDescr?.as_color)
    : null;
};

// Helper function to get initial form data
export const getInitialFormData = (): Partial<Appointment> => {
  return {
    clientAppName: '',
    clientId: undefined,
    assignedTo: undefined,
    clientAppScheduleDateTime: undefined,
    clientAppDuration: undefined,
    clientAppNotes: '',
    inviteEmails: [],
    clientAppStatus: undefined,
  };
};

// Helper function to map item data to form data
export const mapItemToFormData = (item: Appointment): Partial<Appointment> => {
  return {
    clientAppName: item.clientAppName || '',
    clientId: item.clientId,
    assignedTo: item.assignedTo,
    clientAppScheduleDateTime: item.clientAppScheduleDateTime,
    clientAppDuration: item.clientAppDuration,
    clientAppNotes: item.clientAppNotes || '',
    inviteEmails: Array.isArray(item.inviteEmails) ? item.inviteEmails : [],
    clientAppStatus: item.clientAppStatus,
  };
};

// Helper function to render toolbar icon
export const getToolbarIcon = (iconName: string): React.ReactNode => {
  switch (iconName) {
    case 'download':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
      );
    case 'add':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      );
    case 'calendar':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      );
    default:
      return null;
  }
};

// Helper function to render table action buttons
export const getTableActions = (
  item: Appointment,
  onDelete: (id: number) => void,
  colors?: ThemeColors,
  _listContext?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  const linkClass = colors ? cn(colors.primaryFg, colors.interactiveHover, 'p-2 rounded-lg transition-colors') : 'p-2 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-lg transition-colors';
  const editClass = colors ? cn(colors.primaryFg, colors.interactiveHover, 'p-2 rounded-lg transition-colors') : 'p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors';
  return (
    <div className="flex items-center space-x-2 flex-nowrap">
      <Link
        to={moduleConfig.module.route.detail(item.clientAppId)}
        className={linkClass}
        onClick={(e) => e.stopPropagation()}
        title="View"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
      </Link>
      {canEdit && (
        <Link
          to={moduleConfig.module.route.edit(item.clientAppId)}
          className={editClass}
          onClick={(e) => e.stopPropagation()}
          title="Edit"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </Link>
      )}
      {canDelete && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete(item.clientAppId);
          }}
          className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
          title="Delete"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      )}
    </div>
  );
};

export type ModuleConfig = typeof moduleConfig;

